import React, { ChangeEvent, Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Search } from "lucide-react";

import { Checkbox, Radio, TextInput } from "../shared/Inputs";
import { Select, ISelectOption } from "../shared/Selects";
import { EntitlementTableCells, EntitlementTableHeaderCells } from "../sub/EntitlementTableCells";
import { ModalEntitlementForm } from "../sub/ModalEntitlementForm";

import { useGetBulkEntitlementsAvailable, useGetBulkEntitlementsBeneficiaries, useGetBulkEntitlementsPool } from "../../hooks/bulk-entitlements-cax";
import { useGetSubClients } from "../../hooks/sub_clients";
import { useGetCohorts } from "../../hooks/cohorts";

import { EEntitlementSource, EProduct } from "../../types/misc.enum";
import { IAvailableEntitlementsData, IEntitlementsData } from "../../types/misc.interface";
import { IBulkEntitlementsBeneficiaryListItem, IBulkEntitlementsPoolProduct } from "../../types/services.interfaces/bulk-entitlements-cax";

type TBulkEntitlementsSource = 'client' | 'sub-client' | 'cohort';
type TCohortFilterMode = 'all' | 'client-only' | 'sub-client-only';
type TBeneficiaryTypeFilter = EEntitlementSource | 'all';

const SOURCE_OPTIONS: { value: TBulkEntitlementsSource; label: string }[] = [
    { value: 'client', label: 'Client' },
    { value: 'sub-client', label: 'Sub Client' },
    { value: 'cohort', label: 'Cohort' },
];

const COHORT_FILTER_OPTIONS: { value: TCohortFilterMode; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'client-only', label: 'Client Only' },
    { value: 'sub-client-only', label: 'Sub Client Only' },
];

const POOL_TITLES: Record<TBulkEntitlementsSource, string> = {
    'client': 'Client pool',
    'sub-client': 'Sub client pool',
    'cohort': 'Cohort pool',
};

const ENTITLEMENT_SOURCE_MAP: Record<TBulkEntitlementsSource, EEntitlementSource> = {
    'client': EEntitlementSource.CLIENT,
    'sub-client': EEntitlementSource.SUB_CLIENT,
    'cohort': EEntitlementSource.COHORT,
};

const CLIENT_SOURCE_ID = 0;

const EMPTY_ENTITLEMENTS: IEntitlementsData = {
    products: [],
    mbts_modules: [],
    mcsa_modules: [],
    mbts_assessments: 0,
    mcsa_assessments: 0,
};

export const BulkEntitlements = () => {
    const { source } = useParams();
    const navigate = useNavigate();

    const [selectedSubClientId, setSelectedSubClientId] = useState<string | null>(null);
    const [selectedCohortId, setSelectedCohortId] = useState<string | null>(null);

    useEffect(() => {
        if (source !== 'client' && source !== 'sub-client' && source !== 'cohort') {
            navigate('/bulk-entitlements/client', { replace: true });
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source]);

    const isClientSource = source === 'client';
    const isSubClientSource = source === 'sub-client';
    const isCohortSource = source === 'cohort';

    useEffect(() => {
        setSelectedSubClientId(null);
        setSelectedCohortId(null);
    }, [source]);

    const subTitle = useMemo(() => {
        if (isClientSource) {
            return 'Allocate your client pool across direct beneficiaries — sub clients, client-only cohorts, and client-only participants.';
        } else if (isSubClientSource) {
            return 'Allocate your sub client pool across direct beneficiaries — cohorts, and participants.';
        } else if (isCohortSource) {
            return 'Allocate your cohort pool across direct beneficiaries — participants.';
        } else {
            return '';
        };
    }, [isClientSource, isSubClientSource, isCohortSource]);

    const resolvedSourceId = useMemo<number | null>(() => {
        if (isClientSource) return CLIENT_SOURCE_ID;
        if (isSubClientSource) return selectedSubClientId ? Number(selectedSubClientId) : null;
        if (isCohortSource) return selectedCohortId ? Number(selectedCohortId) : null;
        return null;
    }, [isClientSource, isSubClientSource, isCohortSource, selectedSubClientId, selectedCohortId]);

    const handleSourceChange = (nextSource: TBulkEntitlementsSource) => {
        if (nextSource !== source) {
            navigate(`/bulk-entitlements/${nextSource}`);
        };
    };

    return (
        <div className='bulk_entitlements__page'>
            <p className='text-2xl text-semibold'>Distribute Entitlements</p>
            <p className='text-sm color--txt-muted bulk_entitlements__page__subtitle'>
                {subTitle}
            </p>

            <div className="bulk_entitlements__page__source_tabs">
                {SOURCE_OPTIONS.map(option => (
                    <button
                        key={option.value}
                        type="button"
                        className={`bulk_entitlements__page__source_tabs__btn ${source === option.value ? 'active' : ''}`}
                        onClick={() => handleSourceChange(option.value)}
                    >
                        {option.label}
                    </button>
                ))}
            </div>

            {isSubClientSource && (
                <div className="bulk_entitlements__page__selectors">
                    <SubClientSelect
                        selectedSubClientId={selectedSubClientId}
                        onSelect={setSelectedSubClientId}
                    />
                </div>
            )}

            {isCohortSource && <CohortSourceFilters onCohortChange={setSelectedCohortId} />}

            {source && resolvedSourceId !== null && (
                <BulkEntitlementsView
                    source={source as TBulkEntitlementsSource}
                    sourceId={resolvedSourceId}
                />
            )}

            {isSubClientSource && resolvedSourceId === null && (
                <div className="bulk_entitlements__data__empty">
                    <p className="text-medium">Select a sub client to distribute entitlements.</p>
                </div>
            )}

            {isCohortSource && resolvedSourceId === null && (
                <div className="bulk_entitlements__data__empty">
                    <p className="text-medium">Select a cohort to distribute entitlements.</p>
                </div>
            )}
        </div>
    );
};

interface ISubClientSelectProps {
    selectedSubClientId: string | null;
    disabled?: boolean;
    onSelect: (subClientId: string | null) => void;
};
const SubClientSelect = ({ selectedSubClientId, disabled, onSelect }: ISubClientSelectProps) => {
    const { data: subClients, isLoading } = useGetSubClients(true);

    const options = useMemo<ISelectOption[]>(
        () => [
            { value: null, label: 'None' },
            ...(subClients ?? []).map(subClient => ({
                value: subClient.id.toString(),
                label: subClient.name,
            })),
        ],
        [subClients],
    );

    const selected = selectedSubClientId
        ? options.find(option => option.value === selectedSubClientId) ?? null
        : null;

    return (
        <div className="bulk_entitlements__page__selectors__field">
            <span className="text-sm text-medium">Sub Client</span>
            <Select
                selected={selected}
                options={options}
                disabled={disabled || isLoading}
                onSelect={(option) => onSelect(option.value)}
            />
        </div>
    );
};

interface ICohortSourceFiltersProps {
    onCohortChange: (cohortId: string | null) => void;
};
const CohortSourceFilters = ({ onCohortChange }: ICohortSourceFiltersProps) => {
    const [filterMode, setFilterMode] = useState<TCohortFilterMode>('all');
    const [selectedSubClientId, setSelectedSubClientId] = useState<string | null>(null);
    const [selectedCohortId, setSelectedCohortId] = useState<string | null>(null);

    const { data: cohorts, isLoading: isCohortsLoading } = useGetCohorts(true);

    const isSubClientOnly = filterMode === 'sub-client-only';

    const cohortOptions = useMemo<ISelectOption[]>(() => {
        const filtered = (cohorts ?? []).filter(cohort => {
            if (filterMode === 'all') return true;
            if (filterMode === 'client-only') return cohort.sub_client_id === null;
            if (!selectedSubClientId) return false;
            return cohort.sub_client_id?.toString() === selectedSubClientId;
        });

        return filtered.map(cohort => ({
            value: cohort.id.toString(),
            label: cohort.name,
        }));
    }, [cohorts, filterMode, selectedSubClientId]);

    useEffect(() => {
        if (selectedCohortId && !cohortOptions.some(option => option.value === selectedCohortId)) {
            setSelectedCohortId(null);
        };
    }, [cohortOptions, selectedCohortId]);

    useEffect(() => {
        onCohortChange(selectedCohortId);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCohortId]);

    const handleFilterModeChange = (mode: TCohortFilterMode) => {
        setFilterMode(mode);
        setSelectedSubClientId(null);
        setSelectedCohortId(null);
    };

    const handleSubClientSelect = (subClientId: string | null) => {
        setSelectedSubClientId(subClientId);
        setSelectedCohortId(null);
    };

    return (
        <div className="bulk_entitlements__page__filters">
            <div className="bulk_entitlements__page__filters__radios">
                {COHORT_FILTER_OPTIONS.map(option => (
                    <Radio
                        key={option.value}
                        size="sm"
                        label={option.label}
                        value={option.value}
                        checked={filterMode === option.value}
                        onChange={(value) => handleFilterModeChange(value as TCohortFilterMode)}
                    />
                ))}
            </div>

            <div className="bulk_entitlements__page__selectors">
                <SubClientSelect
                    selectedSubClientId={selectedSubClientId}
                    disabled={!isSubClientOnly}
                    onSelect={handleSubClientSelect}
                />
                <CohortSelect
                    options={cohortOptions}
                    selectedCohortId={selectedCohortId}
                    disabled={isCohortsLoading || (isSubClientOnly && !selectedSubClientId)}
                    onSelect={setSelectedCohortId}
                />
            </div>
        </div>
    );
};

interface ICohortSelectProps {
    options: ISelectOption[];
    selectedCohortId: string | null;
    disabled?: boolean;
    onSelect: (cohortId: string | null) => void;
};
const CohortSelect = ({ options, selectedCohortId, disabled, onSelect }: ICohortSelectProps) => {
    const selectOptions = useMemo<ISelectOption[]>(
        () => [{ value: null, label: 'None' }, ...options],
        [options],
    );

    const selected = selectedCohortId
        ? selectOptions.find(option => option.value === selectedCohortId) ?? null
        : null;

    return (
        <div className="bulk_entitlements__page__selectors__field">
            <span className="text-sm text-medium">Cohort</span>
            <Select
                selected={selected}
                options={selectOptions}
                disabled={disabled}
                onSelect={(option) => onSelect(option.value)}
            />
        </div>
    );
};

const BENEFICIARY_TYPE_LABELS: Record<string, string> = {
    [EEntitlementSource.SUB_CLIENT]: 'Sub Client',
    [EEntitlementSource.COHORT]: 'Cohort',
    [EEntitlementSource.CLIENT_USER]: 'Participant',
    [EEntitlementSource.SUB_CLIENT_USER]: 'Participant',
    [EEntitlementSource.COHORT_MEMBER]: 'Participant',
};

const getBeneficiaryTypeLabel = (source: EEntitlementSource) => BENEFICIARY_TYPE_LABELS[source] ?? source;

const getBeneficiaryKey = (beneficiary: IBulkEntitlementsBeneficiaryListItem) => `${beneficiary.source}:${beneficiary.source_id}`;

interface IBulkEntitlementsViewProps {
    source: TBulkEntitlementsSource;
    sourceId: number;
};
const BulkEntitlementsView = ({ source, sourceId }: IBulkEntitlementsViewProps) => {
    const [filterInputValue, setFilterInputValue] = useState<string>('');
    const [typeFilter, setTypeFilter] = useState<TBeneficiaryTypeFilter>('all');
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const [distributionEntitlements, setDistributionEntitlements] = useState<IEntitlementsData>(EMPTY_ENTITLEMENTS);

    const { data: pool, isLoading: isPoolLoading } = useGetBulkEntitlementsPool(source, sourceId);
    const { data: availableEntitlements, isLoading: isAvailableLoading } = useGetBulkEntitlementsAvailable(source, sourceId);
    const { data: beneficiaries, isLoading: isBeneficiariesLoading } = useGetBulkEntitlementsBeneficiaries(source, sourceId);

    const isLoading = isPoolLoading || isAvailableLoading || isBeneficiariesLoading;

    const availableTypes = useMemo<EEntitlementSource[]>(() => {
        const order: EEntitlementSource[] = [
            EEntitlementSource.SUB_CLIENT,
            EEntitlementSource.COHORT,
            EEntitlementSource.CLIENT_USER,
            EEntitlementSource.SUB_CLIENT_USER,
            EEntitlementSource.COHORT_MEMBER,
        ];
        const present = new Set((beneficiaries ?? []).map(beneficiary => beneficiary.source));
        return order.filter(type => present.has(type));
    }, [beneficiaries]);

    useEffect(() => {
        if (typeFilter !== 'all' && !availableTypes.includes(typeFilter as EEntitlementSource)) {
            setTypeFilter('all');
        };
    }, [availableTypes, typeFilter]);

    const filteredBeneficiaries = useMemo(() => {
        if (!beneficiaries) return [];

        return beneficiaries.filter(beneficiary => {
            const matchesType = typeFilter === 'all' || beneficiary.source === typeFilter;
            const searchValue = filterInputValue.toLowerCase();
            const matchesSearch = !searchValue ||
                beneficiary.name.toLowerCase().includes(searchValue) ||
                beneficiary.secondary_label?.toLowerCase().includes(searchValue) ||
                getBeneficiaryTypeLabel(beneficiary.source).toLowerCase().includes(searchValue);

            return matchesType && matchesSearch;
        });
    }, [beneficiaries, filterInputValue, typeFilter]);

    const filteredKeys = useMemo(
        () => filteredBeneficiaries.map(getBeneficiaryKey),
        [filteredBeneficiaries],
    );

    const allFilteredSelected = filteredKeys.length > 0 && filteredKeys.every(key => selectedKeys.includes(key));

    useEffect(() => {
        setSelectedKeys(prev => prev.filter(key => beneficiaries?.some(beneficiary => getBeneficiaryKey(beneficiary) === key)));
    }, [beneficiaries]);

    const handleFilter = (e: ChangeEvent<HTMLInputElement>) => {
        setFilterInputValue(e.target.value);
    };

    const toggleBeneficiary = (key: string) => {
        setSelectedKeys(prev => prev.includes(key)
            ? prev.filter(item => item !== key)
            : [...prev, key]);
    };

    const toggleSelectAllFiltered = () => {
        if (allFilteredSelected) {
            setSelectedKeys(prev => prev.filter(key => !filteredKeys.includes(key)));
        } else {
            setSelectedKeys(prev => Array.from(new Set([...prev, ...filteredKeys])));
        }
    };

    if (isLoading) {
        return (
            <div className='bulk_entitlements__view'>
                <div className="bulk_entitlements__data__loading">
                    <div className='list-loader' />
                    <p className="text-medium">Loading entitlements data...</p>
                </div>
            </div>
        );
    };

    return (
        <div className='bulk_entitlements__view'>
            <PoolSummary source={source} products={pool ?? []} />

            {availableEntitlements && (
                <AvailableEntitlementsView
                    source={ENTITLEMENT_SOURCE_MAP[source]}
                    availableEntitlements={availableEntitlements}
                    onEntitlementsChange={setDistributionEntitlements}
                />
            )}

            <div className="bulk_entitlements__beneficiaries">
                <div className="bulk_entitlements__beneficiaries__header">
                    <p className="text-lg text-semibold">Beneficiaries</p>
                    <p className="text-sm color--txt-muted">
                        {selectedKeys.length} selected
                    </p>
                </div>

                <div className="bulk_entitlements__filters">
                    <TextInput
                        icon={{ icon: <Search />, position: 'prefix' }}
                        placeholder="Search"
                        value={filterInputValue}
                        onChange={handleFilter}
                        disabled={!beneficiaries?.length}
                    />
                    {availableTypes.length > 1 && (
                        <div className="bulk_entitlements__filters__type">
                            <TypeFilterButton
                                label="All"
                                isActive={typeFilter === 'all'}
                                onClick={() => setTypeFilter('all')}
                            />
                            {availableTypes.map(type => (
                                <TypeFilterButton
                                    key={type}
                                    label={`${getBeneficiaryTypeLabel(type)}s`}
                                    isActive={typeFilter === type}
                                    onClick={() => setTypeFilter(type)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <div className="bulk_entitlements__data">
                    {!beneficiaries?.length && (
                        <div className="bulk_entitlements__data__empty">
                            <p className="text-medium">No direct beneficiaries yet.</p>
                            <p className="text-sm color--txt-muted">Add sub clients, cohorts, or participants to distribute entitlements.</p>
                        </div>
                    )}

                    {!!beneficiaries?.length && !filteredBeneficiaries.length && (
                        <div className="bulk_entitlements__data__empty">
                            <p className="text-medium">No beneficiaries found.</p>
                        </div>
                    )}

                    {!!filteredBeneficiaries.length && (
                        <div className="bulk_entitlements__data__table_wrapper">
                            <table>
                                <thead>
                                    <tr>
                                        <th>
                                            <Checkbox
                                                checked={allFilteredSelected}
                                                label=""
                                                onChange={toggleSelectAllFiltered}
                                            />
                                        </th>
                                        <th><p className="text-xs text-medium color--txt-disabled text-left">Type</p></th>
                                        <th><p className="text-xs text-medium color--txt-disabled text-left">Name</p></th>
                                        <EntitlementTableHeaderCells />
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredBeneficiaries.map((beneficiary) => {
                                        const key = getBeneficiaryKey(beneficiary);
                                        return (
                                            <tr key={key}>
                                                <td>
                                                    <Checkbox
                                                        checked={selectedKeys.includes(key)}
                                                        label=""
                                                        onChange={() => toggleBeneficiary(key)}
                                                    />
                                                </td>
                                                <td>
                                                    <p className="text-sm color--txt-muted">{getBeneficiaryTypeLabel(beneficiary.source)}</p>
                                                </td>
                                                <td>
                                                    <p className="text-sm text-medium">{beneficiary.name}</p>
                                                    {beneficiary.secondary_label && (
                                                        <p className="text-xs color--txt-muted">{beneficiary.secondary_label}</p>
                                                    )}
                                                </td>
                                                <EntitlementTableCells entitlements={beneficiary.entitlements} />
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="bulk_entitlements__data__actions">
                    <p className="text-sm color--txt-muted">Distribution save coming next.</p>
                    <button
                        className='btn btn--sm btn--radius-sm btn--primary'
                        disabled
                    >
                        Distribute
                    </button>
                </div>
            </div>
        </div>
    );
};

interface IPoolSummaryProps {
    source: TBulkEntitlementsSource;
    products: IBulkEntitlementsPoolProduct[];
};
const PoolSummary = ({ source, products }: IPoolSummaryProps) => {
    const [activeProductKey, setActiveProductKey] = useState<EProduct | null>(products[0]?.key ?? null);

    useEffect(() => {
        if (!products.length) {
            setActiveProductKey(null);
            return;
        };

        if (!activeProductKey || !products.some(product => product.key === activeProductKey)) {
            setActiveProductKey(products[0].key);
        };
    }, [products, activeProductKey]);

    const activeProductEntry = products.find(product => product.key === activeProductKey) ?? products[0];
    const poolRows = activeProductEntry ? getProductPoolRows(activeProductEntry) : null;

    return (
        <div className="bulk_entitlements__pool_summary">
            <p className="text-lg text-semibold">{POOL_TITLES[source]}</p>
            <div className="bulk_entitlements__pool_summary__card">
                {!products.length && (
                    <p className="text-sm color--txt-muted bulk_entitlements__pool_summary__empty">No products granted to this {source === 'client' ? 'client' : source === 'sub-client' ? 'sub client' : 'cohort'}.</p>
                )}
                {!!products.length && activeProductEntry && poolRows && (
                    <Fragment>
                        <div className="bulk_entitlements__pool_summary__tabs">
                            {products.map(({ key, title }) => (
                                <button
                                    key={key}
                                    type="button"
                                    className={`bulk_entitlements__pool_summary__tabs__btn ${activeProductEntry.key === key ? 'active' : ''}`}
                                    onClick={() => setActiveProductKey(key)}
                                >
                                    {title}
                                </button>
                            ))}
                        </div>
                        <div className="bulk_entitlements__pool_summary__table_wrapper">
                            <table className="bulk_entitlements__pool_summary__table">
                                <thead>
                                    <tr>
                                        <th><p className="text-xs text-medium color--txt-disabled text-left">Title</p></th>
                                        <th><p className="text-xs text-medium color--txt-disabled text-left">Granted</p></th>
                                        <th><p className="text-xs text-medium color--txt-disabled text-left">Allocated</p></th>
                                        <th><p className="text-xs text-medium color--txt-disabled text-left">Available</p></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><p className="text-sm text-medium">Modules</p></td>
                                        <td><p className="text-sm whitespace-pre-line bulk_entitlements__pool_summary__cell--granted">{poolRows.modules.granted}</p></td>
                                        <td><p className="text-sm color--txt-muted whitespace-pre-line">{poolRows.modules.allocated}</p></td>
                                        <td><p className="text-sm whitespace-pre-line bulk_entitlements__pool_summary__cell--available">{poolRows.modules.available}</p></td>
                                    </tr>
                                    <tr>
                                        <td><p className="text-sm text-medium">Assessments</p></td>
                                        <td><p className="text-sm bulk_entitlements__pool_summary__cell--granted">{poolRows.assessments.granted}</p></td>
                                        <td><p className="text-sm color--txt-muted">{poolRows.assessments.allocated}</p></td>
                                        <td><p className="text-sm text-semibold bulk_entitlements__pool_summary__cell--available">{poolRows.assessments.available}</p></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </Fragment>
                )}
            </div>
        </div>
    );
};

interface IAvailableEntitlementsViewProps {
    source: EEntitlementSource;
    availableEntitlements: IAvailableEntitlementsData;
    onEntitlementsChange: (entitlements: IEntitlementsData) => void;
};
const AvailableEntitlementsView = ({ source, availableEntitlements, onEntitlementsChange }: IAvailableEntitlementsViewProps) => (
    <div className="bulk_entitlements__distribution_form">
        <p className="text-lg text-semibold">Available entitlements</p>
        <p className="text-sm color--txt-muted">
            Select products, modules, and total assessments to split evenly across selected beneficiaries.
        </p>
        <ModalEntitlementForm
            isModalOpen={true}
            source={source}
            entitlements={EMPTY_ENTITLEMENTS}
            availableEntitlements={availableEntitlements}
            onEntitlementsChange={onEntitlementsChange}
            isProcessing={false}
            assessmentsTooltip="Total number of {PRODUCT_NAME} assessments to distribute evenly between selected beneficiaries."
            isDivider={false}
            isTitle={false}
        />
    </div>
);

const formatModuleTitles = (modules: { title: string }[]) => {
    if (!modules.length) return '-';
    return modules.map(module => module.title).join('\n');
};

const getProductPoolRows = (productPool: IBulkEntitlementsPoolProduct) => ({
    modules: {
        granted: formatModuleTitles(productPool.granted.modules),
        allocated: formatModuleTitles(productPool.allocated.modules),
        available: formatModuleTitles(productPool.available.modules),
    },
    assessments: {
        granted: productPool.granted.assessments,
        allocated: productPool.allocated.assessments,
        available: productPool.available.assessments,
    },
});

const TypeFilterButton = ({ label, isActive, onClick }: { label: string; isActive: boolean; onClick: () => void; }) => (
    <button
        type="button"
        className={`btn btn--sm btn--radius-sm ${isActive ? 'btn--primary' : 'btn--secondary'}`}
        onClick={onClick}
    >
        {label}
    </button>
);
